import React, { useState } from 'react';
import { excelHeaders, getHeaders } from '.';
import { IListingData } from '@models/listing';
import { downloadExcelFile } from 'src/pages/policy-management/excel/download-json-excel-service';
import ListingTableWithPagination from '@components/table/with-pagination/ListingTableWithPagination';
import { ActionTarget, IEnhancedMenuItem } from '@components/table/types';
import {
  getFleetPolicyList,
  getFleetPolicyWithoutPaginationList,
} from '../../../../queries';
import { useLazyQuery } from '@apollo/client';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { toast } from 'react-toastify';
import { DEFAULT_ERROR_TEXT } from '@utils';
import {
  mapToFleetPolicyListData,
  mapToFleetPolicyListDataToExcel,
} from '../../../../group-utils';
import AddCertificateDrawer from '../add-certificate-drawer/AddCertificateDrawer';
import { enumListAsRecordObject } from '@utils/graph-utils';

interface IFleetGroupCertificateTableWidgetProps {
  className?: string;
  entityId: string;
  currencySymbol: string;
  lineExternalCode: string;
  onUpdateSuccess?: () => void;
}

export const FleetGroupCertificateTableWidget: React.FC<
  IFleetGroupCertificateTableWidgetProps
> = ({ entityId, lineExternalCode, currencySymbol }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [addDocumentDrawerOpen, setAddDocumentDrawerOpen] =
    useState<boolean>(false);
  const [tableData, setTableData] = useState<IListingData>({
    pagedItems: {},
    pageSize: 10,
    pageNumber: 1,
    totalCount: 0,
  });

  const [getData] = useLazyQuery(getFleetPolicyList());
  const [getDownloadData] = useLazyQuery(getFleetPolicyWithoutPaginationList());

  const tableAction: IEnhancedMenuItem[] = [
    {
      title: 'Download',
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onClick: () => {
        getDownloadData({
          variables: {
            id: entityId,
            type: 'CERTIFICATE',
          },
        })
          .then((result) => {
            const data = mapToFleetPolicyListDataToExcel(result.data);
            downloadExcelFile({
              data: data,
              filename: 'Fleet Policy Certificates',
              headers: excelHeaders(currencySymbol),
            });
          })
          .catch(() => {
            toast.error(
              <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
            );
          });
      },
      disabled: false,
      name: 'download',
      target: ActionTarget.Entity,
      rowIndependant: true,
    },
    {
      title: 'Add Certificate',
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onClick: () => {
        setAddDocumentDrawerOpen(true);
      },
      disabled: false,
      name: 'addCertificate',
      target: ActionTarget.Entity,
      rowIndependant: true,
    },
  ];

  const loadData = async (
    currentPage: number,
    pageSize = tableData.pageSize,
    searchKey?: string,
    order?: string,
    orderBy?: string
  ) => {
    setLoading(true);

    try {
      const result = await getData({
        variables: {
          id: entityId,
          currentPage: currentPage,
          currentPageSize: pageSize,
          type: 'CERTIFICATE',
          KeywordSearch: searchKey,
          OrderBy: orderBy
            ? {
                Attribute: orderBy,
                Descending: order === 'desc',
              }
            : null,
        },
        errorPolicy: 'all',
        fetchPolicy: 'no-cache',
      });

      if (result.error) {
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
        return;
      }

      const data = result.data;
      const statuses = enumListAsRecordObject(
        data?.Production_PolicyStatuses?.enumValues
      );

      const newTableData = mapToFleetPolicyListData(data, statuses);
      setTableData({
        ...newTableData,
      });
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = async (
    page: number,
    searchKey?: string,
    rowsPerPage?: number,
    order?: string,
    orderBy?: string
  ) => {
    await loadData(
      page,
      rowsPerPage || tableData.pageSize,
      searchKey,
      order,
      orderBy
    );
  };

  return (
    <>
      <ListingTableWithPagination
        disableSelection
        actions={tableAction}
        name={'certificatesTable'}
        inlineTitle={'Certificates'}
        inline
        headers={getHeaders(currencySymbol)}
        data={tableData}
        onPageChange={handlePageChange}
        loader={loading}
      />
      {addDocumentDrawerOpen && (
        <AddCertificateDrawer
          id={entityId}
          open={addDocumentDrawerOpen}
          lineExternalCode={lineExternalCode}
          onClose={() => {
            setAddDocumentDrawerOpen(false);
          }}
          onSuccess={() => {
            setAddDocumentDrawerOpen(false);
            handlePageChange(1);
          }}
        />
      )}
    </>
  );
};
